'use client';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { env } from '@skin-seoul/env-utils';
import { convertCookieToKey } from '../../utils';
import { hasSessionKeyPersistence, persistCartSessionKey } from '../../utils/sessionKeyPersistence';
import { useEffect } from 'react';
import { useCartSessionKey } from '../../context/cartSessionKeyContext';
import { GetCartRequest } from '../../infrastructure/cart/utils/types';
import { CART_QUERIES } from '../../infrastructure/cart/utils/queries';

const useCartRequiredKeys = () => {
  const { sessionKey } = useCartSessionKey();
  const sessionKeyString = convertCookieToKey(sessionKey);

  if (env('NEXT_PUBLIC_ENV') === 'LOCAL') {
    const loggedInKey = convertCookieToKey({
      name: env('NEXT_PUBLIC_ENV_USERLOGGED_KEY') ?? 'wp_woocommerce_session_9033c79b4978e5a98bbd7a5d580810a0',
      value:
        env('NEXT_PUBLIC_ENV_USERLOGGED_VALUE') ??
        't_44f2dc9b4d7e9ef539a1d401a5cdb5||1756346123||1756342523||e6e0c0bf6c232af719cec94b6abfc4f8',
    });
    return {
      sessionKey: loggedInKey ?? sessionKeyString,
    };
  }
  return {
    sessionKey: sessionKeyString,
  };
};

const useSetSessionKey = (
  generatedSessionKey?: Required<{
    key: string;
    value: string;
  }>,
) => {
  const { setSessionKey } = useCartSessionKey();
  useEffect(() => {
    if (generatedSessionKey?.key && generatedSessionKey?.value) {
      if (hasSessionKeyPersistence()) {
        void persistCartSessionKey({
          name: generatedSessionKey.key,
          value: generatedSessionKey.value,
        });
      } else {
        void (async () => {
          const { setCartSessionKey } = await import('../../utils/cart');
          setCartSessionKey({
            name: generatedSessionKey.key,
            value: generatedSessionKey.value,
          });
        })();
      }
      setSessionKey({
        name: generatedSessionKey.key,
        value: generatedSessionKey.value,
      });
    }
  }, [setSessionKey, generatedSessionKey]);
};

// Note: To ensure that useMutation registers and unregisters subscribers properly,
// the queryClient must be obtained via the useQueryClient() hook at the time of hook invocation.
// Simply injecting queryClient via a factory function bypasses React Query's lifecycle management
// and can lead to memory leaks.
export const useCartService = () => {
  const useGetCartSuspenseQuery = (params: GetCartRequest) => {
    const { sessionKey } = useCartRequiredKeys();

    const query = useSuspenseQuery(CART_QUERIES.getCart({ ...params, sessionKey }));
    useSetSessionKey(query.data?.generatedSessionKey);

    return query;
  };
  const useGetCartQuery = (params: GetCartRequest) => {
    const { sessionKey } = useCartRequiredKeys();

    const query = useQuery(CART_QUERIES.getCart({ ...params, sessionKey }));
    useSetSessionKey(query.data?.generatedSessionKey);
    return query;
  };

  const useAddToCart = (params: GetCartRequest) => {
    const queryClient = useQueryClient();
    const { sessionKey } = useCartRequiredKeys();

    const mutation = useMutation(CART_QUERIES.addToCart({ ...params, sessionKey, queryClient }));

    useSetSessionKey(mutation.data?.generatedSessionKey);

    return mutation;
  };

  const useUpdateCartItem = (params: GetCartRequest) => {
    const queryClient = useQueryClient();
    const { sessionKey } = useCartRequiredKeys();

    const mutation = useMutation(CART_QUERIES.updateCartItem({ ...params, sessionKey, queryClient }));

    useSetSessionKey(mutation.data?.generatedSessionKey);

    return mutation;
  };

  const useDeleteCartItem = (params: GetCartRequest) => {
    const queryClient = useQueryClient();
    const { sessionKey } = useCartRequiredKeys();

    const mutation = useMutation(CART_QUERIES.deleteCartItem({ ...params, sessionKey, queryClient }));

    useSetSessionKey(mutation.data?.generatedSessionKey);

    return mutation;
  };

  const useApplyCoupon = (params: GetCartRequest) => {
    const queryClient = useQueryClient();
    const { sessionKey } = useCartRequiredKeys();

    const mutation = useMutation(CART_QUERIES.applyCoupon({ ...params, sessionKey, queryClient }));

    useSetSessionKey(mutation.data?.generatedSessionKey);

    return mutation;
  };

  const useRemoveCoupon = (params: GetCartRequest) => {
    const queryClient = useQueryClient();
    const { sessionKey } = useCartRequiredKeys();

    const mutation = useMutation(CART_QUERIES.removeCoupon({ ...params, sessionKey, queryClient }));

    useSetSessionKey(mutation.data?.generatedSessionKey);

    return mutation;
  };

  return {
    useGetCartSuspenseQuery,
    useGetCartQuery,
    useAddToCart,
    useUpdateCartItem,
    useDeleteCartItem,
    useApplyCoupon,
    useRemoveCoupon,
  };
};
